import { expandBaseWebBorder, expandBorderRadii, paddingUtil } from "@gadgetinc/themes/src/utils";
import { useStyletron } from "baseui";
import type { Theme } from "baseui/theme";
import type { ReactNode } from "react";
import React from "react";
import { StyleObject } from "styletron-standard";
import { Column } from "../../Column";
import { Row } from "../../Row";
import { Text } from "../../Text";
import { Button, KIND as ButtonKIND } from "../editor/Button";
import { KIND, getIconForKind } from "./Note";
import { IconButton } from "./IconButton";
import { CancelIcon } from "@gadgetinc/themes/src/icons/CancelIcon";
export { KIND } from "./Note";

export interface CallToActionProps {
  content: ReactNode;
  onClick?: ((event: React.MouseEvent<HTMLButtonElement>) => void) | ((event: React.MouseEvent<HTMLButtonElement>) => Promise<void>);
  startEnhancer?: ReactNode;
  href?: string;
  target?: string;
}

export const StatusBanner = (props: {
  title?: string;
  content: ReactNode;
  KIND?: KIND;
  primaryCallToAction?: CallToActionProps;
  secondaryCallToAction?: CallToActionProps;
  $style?: StyleObject;
  onDismiss?: () => void;
}) => {
  const [_, $theme] = useStyletron();
  const icon = getIconForKind($theme, props.KIND);
  const styles = getStylesForKind($theme, props.KIND);

  return (
    <Row $gap={$theme.sizing.scale300} $style={{ ...styles.card, ...props.$style }}>
      <Row $gap={$theme.sizing.scale300} $style={{ flex: 1 }}>
        <Row
          $style={{
            alignSelf: "stretch",
            alignItems: "flex-start",
            ...paddingUtil($theme.sizing.scale0, $theme.sizing.scale100),
          }}
        >
          {icon}
        </Row>
        <Row $gap={$theme.sizing.scale600} $align="space-between" $style={{ flex: 1 }}>
          <Column $style={{ flex: 1 }}>
            {props.title && <Text $style={{ fontWeight: 700 }}>{props.title}</Text>}
            {props.content}
          </Column>
          <Row $gap={$theme.sizing.scale300}>
            {props.onDismiss && (
              <IconButton label="Dismiss" kind={ButtonKIND.minimal} size="square" type="button" onClick={props.onDismiss}>
                <CancelIcon />
              </IconButton>
            )}
            {props.secondaryCallToAction && (
              <Button
                startEnhancer={props.secondaryCallToAction.startEnhancer}
                kind="secondary"
                onClick={props.secondaryCallToAction.onClick}
                $style={styles.button.secondary}
                $as={props.secondaryCallToAction.href ? "a" : undefined}
                href={props.secondaryCallToAction.href}
                target={props.secondaryCallToAction.target}
              >
                {props.secondaryCallToAction.content}
              </Button>
            )}
            {props.primaryCallToAction && (
              <Button
                startEnhancer={props.primaryCallToAction.startEnhancer}
                kind="primary"
                onClick={props.primaryCallToAction.onClick}
                $style={styles.button.primary}
                $as={props.primaryCallToAction.href ? "a" : undefined}
                href={props.primaryCallToAction.href}
                target={props.primaryCallToAction.target}
              >
                {props.primaryCallToAction.content}
              </Button>
            )}
          </Row>
        </Row>
      </Row>
    </Row>
  );
};
StatusBanner.displayName = "StatusBanner";

const getStylesForKind = <T extends Theme>($theme: T, kind?: KIND) => {
  let backgroundColor = $theme.colors.backgroundPositive;
  let borderColor = $theme.colors.borderPositive;
  let primaryButtonColor = $theme.colors.buttonPrimaryFill;
  let primaryButtonHoverColor = $theme.colors.buttonPrimaryHover;
  let secondaryButtonColor = $theme.colors.buttonPrimaryFill;
  let secondaryButtonHoverColor = $theme.colors.buttonPrimaryHover;

  switch (kind) {
    case KIND.positive:
      backgroundColor = $theme.colors.backgroundPositive;
      borderColor = $theme.colors.borderPositive;
      primaryButtonColor = $theme.colors.green600;
      primaryButtonHoverColor = $theme.colors.green700;
      secondaryButtonColor = $theme.colors.green200;
      secondaryButtonHoverColor = $theme.colors.green300;
      break;
    case KIND.warning:
      backgroundColor = $theme.colors.backgroundWarning;
      borderColor = $theme.colors.borderWarning;
      primaryButtonColor = $theme.colors.yellow600;
      primaryButtonHoverColor = $theme.colors.yellow700;
      secondaryButtonColor = $theme.colors.yellow200;
      secondaryButtonHoverColor = $theme.colors.yellow300;
      break;
    case KIND.negative:
      backgroundColor = $theme.colors.backgroundNegative;
      borderColor = $theme.colors.borderNegative;
      primaryButtonColor = $theme.colors.red600;
      primaryButtonHoverColor = $theme.colors.red700;
      secondaryButtonColor = $theme.colors.red200;
      secondaryButtonHoverColor = $theme.colors.red300;
      break;
    case KIND.info:
      backgroundColor = $theme.colors.backgroundInfo;
      borderColor = $theme.colors.borderInfo;
      primaryButtonColor = $theme.colors.blue600;
      primaryButtonHoverColor = $theme.colors.blue700;
      secondaryButtonColor = $theme.colors.blue200;
      secondaryButtonHoverColor = $theme.colors.blue300;
      break;
    default:
      backgroundColor = $theme.colors.primary50;
      borderColor = $theme.colors.primary200;
      break;
  }

  return {
    card: {
      ...expandBaseWebBorder({
        ...$theme.borders.border100,
        borderColor,
      }),
      ...expandBorderRadii($theme.borders.surfaceBorderRadius),
      ...paddingUtil($theme.sizing.scale500, $theme.sizing.scale600),
      justifyContent: "space-between",
      backgroundColor,
    },
    button: {
      primary: {
        backgroundColor: primaryButtonColor,
        ":hover": {
          backgroundColor: primaryButtonHoverColor,
        },
      },
      secondary: {
        backgroundColor: secondaryButtonColor,
        ":hover": {
          backgroundColor: secondaryButtonHoverColor,
        },
      },
    },
  };
};
